import type { PinnedVersionSelection } from "./pinned-version-select";

import type { SharedSession } from "@/queries/prompts";

/** Default pinned selection when comparing the editor’s current version to the latest saved version. */
export const defaultPinnedVersionSelection = (
  currentVersionNumber: number | null | undefined,
  latestVersionNumber: number | undefined,
): PinnedVersionSelection => {
  if (!currentVersionNumber || latestVersionNumber === undefined) {
    return { type: "latest" };
  }
  if (currentVersionNumber === latestVersionNumber) {
    return { type: "latest" };
  }
  return { type: "version", versionNumber: currentVersionNumber };
};

/** Maps pinned-version UI selection to optional POST body fields (omit keys for “latest”). */
export const pinnedVersionSelectionToCreateBodyFields = (
  selection: PinnedVersionSelection,
): Record<string, string | number> => {
  switch (selection.type) {
    case "version":
      return { version_number: selection.versionNumber };
    case "label":
      return { label_name: selection.labelName };
    default:
      return {};
  }
};

export const deriveSharedSessionVersionSelection = (
  session: SharedSession,
): PinnedVersionSelection => {
  if (session.label_name) {
    return { type: "label", labelName: session.label_name };
  }
  if (session.version_number) {
    return { type: "version", versionNumber: session.version_number };
  }
  return { type: "latest" };
};

export const sharedSessionVersionDisplayText = (session: SharedSession): string | null => {
  if (session.label_name) {
    return session.label_name;
  }
  if (session.version_number) {
    return `Version ${session.version_number}`;
  }
  return null;
};

/** Maps pinned-version UI selection to API fields on `SharedSession` PATCH. */
export const buildVersionUpdateFields = (
  selection: PinnedVersionSelection,
): Pick<SharedSession, "label_name" | "version_number"> => {
  switch (selection.type) {
    case "latest":
      return { label_name: null, version_number: null };
    case "label":
      return { label_name: selection.labelName, version_number: null };
    case "version":
      return { label_name: null, version_number: selection.versionNumber };
    default:
      return { label_name: null, version_number: null };
  }
};
